import type { AxiosResponse } from 'axios';
import { BaseService } from './BaseService';
import type { AgentSessionFileDto, AgentSessionFileVersionDto, FileCategory } from '../types/file';

export type UploadProgressCallback = (progressPercent: number) => void;

/**
 * File service for handling session file operations.
 */
export class FileService extends BaseService {
  /**
   * Lists all files for a session.
   */
  async getSessionFiles(sessionId: string): Promise<AgentSessionFileDto[]> {
    return this.get<AgentSessionFileDto[]>(`/sessions/${sessionId}/files`);
  }

  /**
   * Gets file metadata by file id.
   */
  async getFile(id: string): Promise<AgentSessionFileDto> {
    return this.get<AgentSessionFileDto>(`/files/${id}`);
  }

  /**
   * Uploads a file to a session.
   */
  async uploadFile(
    sessionId: string,
    file: File,
    category: FileCategory,
    options?: {
      description?: string;
      metadataJson?: string;
      onProgress?: UploadProgressCallback;
    }
  ): Promise<AgentSessionFileDto> {
    const formData = new FormData();
    formData.append('file', file);
    // ASP.NET will bind enums from numeric strings by default.
    formData.append('category', String(category));
    if (options?.description) formData.append('description', options.description);
    if (options?.metadataJson) formData.append('metadataJson', options.metadataJson);

    return this.post<AgentSessionFileDto>(`/sessions/${sessionId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (evt: any) => {
        const total = evt?.total ?? evt?.event?.total;
        const loaded = evt?.loaded ?? evt?.event?.loaded;
        if (!total || !loaded) return;
        const percent = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
        options?.onProgress?.(percent);
      },
    });
  }

  /**
   * Deletes a file by id.
   */
  async deleteFile(id: string): Promise<void> {
    return this.delete<void>(`/files/${id}`);
  }

  /**
   * Gets version history for a file.
   */
  async getFileVersions(id: string): Promise<AgentSessionFileVersionDto[]> {
    return this.get<AgentSessionFileVersionDto[]>(`/files/${id}/versions`);
  }

  /**
   * Downloads a file as a Blob.
   */
  async downloadFileBlob(id: string): Promise<AxiosResponse<Blob>> {
    return this.client.get<Blob>(`/files/${id}/download`, {
      responseType: 'blob',
    });
  }
}

export const fileService = new FileService();

